import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { UserFilterColumnDisplayed } from '../../../models/user-filter-columndisplayed.model';
import { ColumnDescription, UserReportFilter } from '../../../models/user-report-filter.model';
import { LocalStorageService } from '../../../services/infrastructure/local-storage.service';
import { UserReportFilterService } from '../../../services/seed/reports/user-report-filter.service';
import { MasterConstants } from '../../constants/master-constants';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { UserReportFilterSliderService } from './user-report-filter-slider.service';
import { ReportType } from '../../../models/report-type.model';
@Component({
    selector: 'app-user-report-filter',
    templateUrl: './user-report-filter.component.html',
    styleUrls: ['./user-report-filter.component.scss']
})
export class UserReportFilterComponent implements OnInit, OnDestroy {
    @ViewChild('filterTable', { read: MatSort }) filtertSort: MatSort;
    @ViewChild('filterPaginator', { read: MatPaginator }) filterPaginator: MatPaginator;

    private subscription = new Array<Subscription>();
    private fetchedFilters: Array<UserReportFilter>;
    private userId: string;
    private filterComponents: UserFilterColumnDisplayed;

    public userReportFilterDataSource: MatTableDataSource<any>;
    public displayedColumns: string[] = ['lastRunByTimeStamp', 'filterDescription', 'contextMenu'];
    public currentFilter = new UserReportFilter();
    public originalDisplayedColumns: Array<ColumnDescription>;
    public selectedDisplayedColumns: Array<ColumnDescription>;
    public mode: number = 0;
    public searchPipe = new BehaviorSubject<string>(null);
    public reportName: FormControl;
    public filterValueList = new Array<string>();
    public reportEnum: typeof ReportType = ReportType;

    public get reportNameUsedValidation(): boolean {
        return this.reportName.invalid && (this.reportName.dirty || this.reportName.touched);
    }

    public get disableCreateReport(): boolean {
        return (!this.filterComponents.filterOption.AccountNumber || this.filterValueList.length === 0) && this.currentFilter.reportTypeId === 2;
    }

    constructor(
        private userReportFilterService: UserReportFilterService,
        private userReportFilterSliderService: UserReportFilterSliderService,
        private localStorageService: LocalStorageService,
        private toastrService: ToastrService,
        private matDialog: MatDialog,
    ) {
    }

    public ngOnInit(): void {
        const company = MasterConstants.US_COMPANYCODE;
        const users = JSON.parse(this.localStorageService.get(MasterConstants.EVOLVE_ACCESS));

        this.reportName = new FormControl('', [Validators.required, this.userNameUsedValidator(this)]);

        if (company && users) {
            this.userId = users.resultData.find(x => x.userIdCompanyCode === company).userId;

            this.loadData();
        }
    }

    public getErrorMessage() {
        if (this.reportName.hasError('required')) {
            return 'Please enter the report name.';
        }

        if (this.reportName.hasError('userNameUsed')) {
            return 'Report name already used.Please provide a different name.';
        }
    }

    public userNameUsedValidator = (that: UserReportFilterComponent): ValidatorFn => (control: AbstractControl): ValidationErrors | null => {
        if (that.fetchedFilters && that.fetchedFilters.filter(x => x.filterDescription.toUpperCase().trim() === (control.value as string).toUpperCase().trim()).length !== 0 && that.currentFilter && that.currentFilter.filterDescription.toUpperCase().trim() !== (control.value as string).toUpperCase().trim()) {
            return { userNameUsed: true }
        }

        return null;
    }

    public searchReportFilter(): void {
        this.subscription.push(
            this.searchPipe.pipe(
                debounceTime(500),
                distinctUntilChanged(),
                switchMap((search: string) => {
                    if (search)
                        return of(search.trim().toUpperCase());
                    else
                        return of('');
                })
            ).subscribe(search => {
                const filters = this.fetchedFilters.filter(x => x.filterDescription.toUpperCase().search(search) !== -1);
                this.setDS(filters);
            })
        );
    }

    public loadData(): void {
        this.subscription.push(
            this.userReportFilterSliderService.selectedUserFilterReq.pipe(switchMap(x => {
                this.filterComponents = JSON.parse((x.filter as UserReportFilter).reportFilter);

                this.currentFilter = x.filter;

                this.currentFilter.reportFilter = (x.filter as UserReportFilter).reportFilter;
                this.currentFilter.userId = this.userId;
                this.currentFilter.reportTypeId = x.reportTypeId;
                this.currentFilter.filterDescription = (x.filter as UserReportFilter).filterDescription ?? '';

                this.originalDisplayedColumns = x.originalColumns;
                this.selectedDisplayedColumns = this.filterComponents.displayedColumns ?? new Array<ColumnDescription>();
                this.mode = x.mode;
                this.filterValueList = this.filterComponents.filterValueList;

                if (this.currentFilter.filterDescription)
                    this.reportName.setValue(this.currentFilter.filterDescription);

                return this.userReportFilterService.getUserReportFilters(this.userId, x.reportTypeId);
            })).subscribe(filters => {
                this.fetchedFilters = filters;
                this.setDS(filters.sort((one, two) => (two.filterDescription > one.filterDescription ? -1 : 1)));
                this.searchReportFilter();
            }));
    }

    public runReport(filter: UserReportFilter): void {
        filter.isReportRun = true;

        if (filter.userReportFilterId) {
            this.subscription.push(this.userReportFilterService.updateUserReportFilters(filter).subscribe(updatedFilter => {
                if (updatedFilter) {
                    this.userReportFilterSliderService.setData(updatedFilter);
                    this.userReportFilterSliderService.userFilterClose();
                }
            }));
        }
        else {
            filter.filterDescription = this.reportName.value;
            this.userReportFilterSliderService.setData(filter);
            this.userReportFilterSliderService.userFilterClose();
        }
    }

    public copyReport(filter: UserReportFilter): void {
        this.currentFilter = { ...filter };
        this.currentFilter.userReportFilterId = null;
        this.currentFilter.filterDescription = '';
        this.reportName.setValue(this.currentFilter.filterDescription);

        this.filterComponents = JSON.parse(filter.reportFilter);

        this.selectedDisplayedColumns = this.filterComponents.displayedColumns;
        this.filterValueList = this.filterComponents.filterValueList;

        this.mode = 1;
    }

    public updateReport(filter: UserReportFilter): void {
        this.currentFilter = filter;
        this.reportName.setValue(this.currentFilter.filterDescription);
        this.filterComponents = JSON.parse(filter.reportFilter);

        this.selectedDisplayedColumns = this.filterComponents.displayedColumns;
        this.filterValueList = this.filterComponents.filterValueList;

        this.mode = 1;
    }

    public newReport(): void {
        this.currentFilter.userReportFilterId = null;
        this.currentFilter.filterDescription = '';

        this.mode = 1;
    }

    public editReport(selectedFilter: UserReportFilter): void {
        this.subscription.push(this.userReportFilterService.updateUserReportFilters(selectedFilter).subscribe(filter => {
            if (filter) {
                this.userReportFilterSliderService.setData(filter);
                this.userReportFilterSliderService.userFilterClose();
                this.toastrService.success("Records saved successfully.");
            }
        }));
    }

    public selectDisplayedColumns(columns: Array<ColumnDescription>): void {
        this.filterComponents.displayedColumns = columns;
        this.currentFilter.reportFilter = JSON.stringify(this.filterComponents);
    }

    public addReport(): void {
        this.currentFilter.filterDescription = this.reportName.value;

        if (this.currentFilter.userReportFilterId) {
            this.editReport(this.currentFilter);
        }
        else {
            this.subscription.push(this.userReportFilterService.addUserReportFilters(this.currentFilter).subscribe(filter => {
                this.userReportFilterSliderService.setData(filter);
                this.userReportFilterSliderService.userFilterClose();
                this.toastrService.success("Records saved successfully.");
            }));
        }
    }

    public deleteReport(selectedFilter: UserReportFilter): void {
        this.subscription.push(
            this.deleteConfirmation(selectedFilter).pipe(switchMap(result => {
                if (result) {
                    return this.userReportFilterService.deleteUserReportFilters(selectedFilter)
                }
            })).subscribe(filter => {
                if (filter) {
                    filter.reportFilter = '';
                    this.userReportFilterSliderService.setData(filter);
                    this.loadData();
                }
            })
        );
    }

    private deleteConfirmation(selectedFilter: UserReportFilter): Observable<boolean> {
        const datepipe: DatePipe = new DatePipe('en-US');

        let message = '';

        if (selectedFilter.reportTypeId == ReportType.AMSPricing || selectedFilter.reportTypeId == ReportType.SulfurAndZincReport) {
            message = `
            <div class="row">
              <div class="col-md-12 padding-l55">
                <b>Report Name:</b> ${selectedFilter.filterDescription}       <b>Last Run Date:</b> ${datepipe.transform((selectedFilter.lastRunByTimeStamp || selectedFilter.createTimeStamp), 'dd/MM/YYYY')}
                <br><br><b>Deleting this report will result in corresponding subscription (if present), to be also removed.<b>
              </div>
            </div>`;
        } else {
            message =
                `
            <div class="row">
              <div class="col-md-12 padding-l55">
                <b>Report Name:</b> ${selectedFilter.filterDescription}       <b>Last Run Date:</b> ${datepipe.transform((selectedFilter.lastRunByTimeStamp || selectedFilter.createTimeStamp), 'dd/MM/YYYY')}
              </div>
            </div>`;
        }

        return this.matDialog.open(ConfirmDialogComponent, {
            disableClose: true,
            width: '480px',
            data: { title: 'Delete Report', htmlMessage: message, labelYes: 'Delete Report', labelNo: 'Cancel', dialogType: 'confirm' }
        }).afterClosed();
    }

    public deleteCurrentReport(selectedFilter: UserReportFilter): void {
        this.subscription.push(
            this.deleteConfirmation(selectedFilter).pipe(switchMap(result => {
                if (result) {
                    return this.userReportFilterService.deleteUserReportFilters(selectedFilter)
                }
            })).subscribe(filter => {
                this.userReportFilterSliderService.resetFilter();
                this.userReportFilterSliderService.userFilterClose();
            }));
    }

    private setDS(source: any[]) {
        this.userReportFilterDataSource = new MatTableDataSource(source);
        this.userReportFilterDataSource.paginator = this.filterPaginator;
        this.userReportFilterDataSource.sort = this.filtertSort;
    }

    public ngOnDestroy(): void {
        this.subscription.forEach(subscription => subscription.unsubscribe());
    }
}
